import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Form, FormGroup, Label, Input, Button, Alert } from 'reactstrap';
import Loader from './Loader';
import api from './utils/api';

function WalletTransactionForm({ accessToken, contactId, signature, messageSender }) {
	// loading | cancelled | no_pass | error | ready
	const [status, setStatus] = useState('loading');
	const [errorMessage, setErrorMessage] = useState('');
	const [context, setContext] = useState(null); // { commercePoolId, expirationDate, walletId, currencyCode }

	const [amount, setAmount] = useState('');
	const [notes, setNotes] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState('');
	const [submitted, setSubmitted] = useState(false);

	useEffect(() => {
		if (!accessToken || !contactId) return;

		let cancelled = false;

		async function load() {
			try {
				// Find the oldest pass for this contact
				const passes = await api.listOldestPass(contactId, accessToken);
				const pass = passes && passes.content && passes.content[0];
				if (!pass) {
					if (!cancelled) setStatus('no_pass');
					return;
				}
				if (pass.state === 'CANCELLED') {
					if (!cancelled) setStatus('cancelled');
					return;
				}

				const [passPlan, contact] = await Promise.all([
					api.getPassPlan(pass.pass_plan.id, accessToken),
					api.getContact(contactId, accessToken),
				]);

				const walletId = contact && contact.wallet && contact.wallet.id;
				if (!walletId) {
					throw new Error('Contact has no CRM.COM Wallet.');
				}

				const [balances, contactJournals, walletJournals] = await Promise.all([
					api.getWalletCommerceBalances(walletId, accessToken),
					api.listPassRedemptionJournals(contactId, accessToken),
					api.listWalletPassRedemptionJournals(walletId, accessToken),
				]);

				// Prefer an already-credited commerce pool on the wallet; fall back to the pass plan's pool
				const existingPool = balances && balances.content && balances.content[0];
				const commercePoolId = existingPool ? existingPool.id : (passPlan.commerce_pool && passPlan.commerce_pool.id);

				if (!commercePoolId) {
					throw new Error('No commerce pool could be resolved for this contact.');
				}

				// The contact-scoped journals endpoint links a journal back to the pass it came from
				// (financial_event.id) but has no expiration_date; the wallet-scoped journals endpoint
				// has expiration_date but no link back to the pass. Match the two by journal id.
				const matchingContactJournal = contactJournals && contactJournals.content
					&& contactJournals.content.find((journal) => journal.financial_event && journal.financial_event.id === pass.id);
				const matchingWalletJournal = matchingContactJournal && walletJournals && walletJournals.content
					&& walletJournals.content.find((journal) => journal.id === matchingContactJournal.id);

				if (!cancelled) {
					setContext({
						commercePoolId,
						expirationDate: matchingWalletJournal ? matchingWalletJournal.expiration_date : null,
						walletId,
						currencyCode: pass.currency_code || 'EUR',
					});
					setStatus('ready');
				}
			} catch (error) {
				if (!cancelled) {
					setErrorMessage(error.message || 'Failed to load wallet information.');
					setStatus('error');
				}
			}
		}

		load();

		return () => { cancelled = true; };
	}, [accessToken, contactId]);

	const handleClose = () => {
		messageSender(JSON.stringify({ signature, message: 'COMPLETED' }));
	};

	const formatDate = (epochSeconds) => {
		return new Date(epochSeconds * 1000).toLocaleDateString(undefined, {
			year: 'numeric', month: 'long', day: 'numeric',
		});
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		const numericAmount = Number(amount);
		if (!numericAmount || numericAmount <= 0) {
			setSubmitError('Please enter a valid amount.');
			return;
		}

		setSubmitting(true);
		setSubmitError('');
		try {
			const payload = {
				commerce_pool_id: context.commercePoolId,
				amount: numericAmount.toFixed(2),
				notes: notes || '',
				custom_fields: [],
				action: 'CREDIT',
				entity_id: context.walletId,
				entity: 'WALLET',
				currency_code: context.currencyCode,
			};
			// Only pin validity to the pass's expiration when it has one; otherwise leave
			// valid_from/valid_to unset so the commerce pool's own validity applies.
			if (context.expirationDate) {
				payload.valid_from = Math.floor(Date.now() / 1000);
				payload.valid_to = context.expirationDate;
			}
			await api.createWalletJournal(contactId, payload, accessToken);
			setSubmitted(true);
		} catch (error) {
			setSubmitError(error.message || 'Failed to create the wallet transaction.');
		}
		setSubmitting(false);
	};

	if (status === 'loading') {
		return <Loader />;
	}

	if (status === 'cancelled') {
		return (
			<Container className="mt-4 text-center">
				<Alert color="danger">Pass is cancelled. Cannot perform any wallet transactions</Alert>
				<Button color="secondary" onClick={handleClose}>Close</Button>
			</Container>
		);
	}

	if (status === 'no_pass') {
		return (
			<Container className="mt-4 text-center">
				<Alert color="warning">No pass was found for this contact.</Alert>
				<Button color="secondary" onClick={handleClose}>Close</Button>
			</Container>
		);
	}

	if (status === 'error') {
		return (
			<Container className="mt-4 text-center">
				<Alert color="danger">{errorMessage}</Alert>
				<Button color="secondary" onClick={handleClose}>Close</Button>
			</Container>
		);
	}

	if (submitted) {
		return (
			<Container className="mt-4 text-center">
				<Alert color="success">Wallet transaction created successfully.</Alert>
				<Button color="secondary" onClick={handleClose}>Close</Button>
			</Container>
		);
	}

	return (
		<Container className="mt-4">
			{submitting && <Loader />}
			<Row className="align-items-center">
				<Col xs="2" />
				<Col className="text-center">
					<h3 className="fw-bold mb-0">Credit Wallet</h3>
				</Col>
				<Col xs="2" className="d-flex justify-content-end">
					<Button color="secondary" onClick={handleClose}>Close</Button>
				</Col>
			</Row>
			<hr />
			<Alert color="info">
				{context.expirationDate
					? <>This wallet credit will be valid until <strong>{formatDate(context.expirationDate)}</strong>, matching the initial wallet credit expiration.</>
					: <>This wallet credit has <strong>no expiration date</strong> — its validity will follow the commerce pool's own settings.</>}
			</Alert>
			<Form onSubmit={handleSubmit}>
				<Row>
					<Col xs="4">
						<FormGroup>
							<Label for="amount">Amount</Label>
							<Input
								id="amount"
								type="number"
								step="0.01"
								min="0"
								value={amount}
								onChange={(event) => setAmount(event.target.value)}
								placeholder="0.00"
								required
							/>
						</FormGroup>
					</Col>
					<Col xs="8">
						<FormGroup>
							<Label for="notes">Notes</Label>
							<Input
								id="notes"
								type="textarea"
								rows="1"
								value={notes}
								onChange={(event) => setNotes(event.target.value)}
							/>
						</FormGroup>
					</Col>
				</Row>
				{submitError && <Alert color="danger">{submitError}</Alert>}
				<div className="d-flex justify-content-center">
					<Button className="btn-credit-wallet" type="submit" disabled={submitting}>Submit</Button>
				</div>
			</Form>
		</Container>
	);
}

export default WalletTransactionForm;
